import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { MAP_CHANGE_STATUS } from "../../enums/map";
import { deriveMapChanges } from "./mapChangeFactory";
import {
  buildMapPageFacts,
  groupMapFacts,
  relatedGroupLabels,
} from "./mapDerivation";
import type {
  MapChange,
  MapChangeEdit,
  MapCurrentGroup,
  MapPlan,
  MapTargetItem,
} from "./mapTypes";
import { mapVocabulary } from "./mapVocabulary";

export function buildMapPlan(data: OverviewResponse): MapPlan {
  const facts = buildMapPageFacts(data);
  const currentGroups = groupMapFacts(facts);
  const vocabulary = mapVocabulary(data);
  const changes = deriveMapChanges({
    data,
    facts,
    groups: currentGroups,
    vocabulary,
  });
  return {
    data,
    facts,
    currentGroups,
    changes,
    vocabulary,
    emptyReason: mapEmptyReason(data, changes),
  };
}

export function applyMapEdits(
  changes: MapChange[],
  edits: Record<string, MapChangeEdit>,
): MapChange[] {
  return changes.map((change) => ({
    ...change,
    ...(edits[change.id] ?? {}),
  }));
}

function activeMapChanges(changes: MapChange[]): MapChange[] {
  return changes.filter(
    (change) => change.status !== MAP_CHANGE_STATUS.DISMISSED,
  );
}

export function buildTargetEstate(
  plan: MapPlan,
  changes: MapChange[],
): MapTargetItem[] {
  const changedGroups = new Set(
    plan.changes.map((change) => change.currentGroupKey),
  );
  const changeItems = activeMapChanges(changes).map(
    (change) =>
      ({
        id: `target-${change.id}`,
        lane: change.targetLane,
        label:
          change.targetGroup === change.currentGroupLabel
            ? change.title
            : change.targetGroup,
        detail: change.targetRole,
        relationship: relationshipLabel(plan, change),
        pageCount: change.pages.length,
        state: change.status,
        changeId: change.id,
        adjusted: change.adjusted,
      }) satisfies MapTargetItem,
  );
  const stableItems = plan.currentGroups
    .filter((group) => !changedGroups.has(group.key))
    .slice(0, Math.max(2, 8 - changeItems.length))
    .map(stableTargetItem);
  return [...changeItems, ...stableItems].slice(0, 10);
}

export function mapWorkflowCounts(changes: MapChange[]) {
  const active = activeMapChanges(changes);
  return {
    proposed: active.length,
    ready: active.filter(
      (change) => change.status === MAP_CHANGE_STATUS.ACCEPTED,
    ).length,
    review: active.filter(
      (change) =>
        change.status === MAP_CHANGE_STATUS.PENDING ||
        change.status === MAP_CHANGE_STATUS.ADJUSTED,
    ).length,
    unresolved: active.filter(
      (change) => change.status === MAP_CHANGE_STATUS.UNRESOLVED,
    ).length,
  };
}

function stableTargetItem(group: MapCurrentGroup): MapTargetItem {
  const roleCounts = new Map<string, number>();
  for (const fact of group.facts) {
    roleCounts.set(fact.role, (roleCounts.get(fact.role) ?? 0) + 1);
  }
  const dominant = [...roleCounts.entries()].sort(
    ([leftRole, leftCount], [rightRole, rightCount]) =>
      rightCount - leftCount || leftRole.localeCompare(rightRole),
  )[0]?.[0];
  const lane =
    dominant === "action" || dominant === "destination"
      ? "destination"
      : dominant === "content"
        ? "pattern"
        : "organise";
  return {
    id: `target-current-${group.key}`,
    lane,
    label: group.label,
    detail: `${group.facts.length} current page${group.facts.length === 1 ? "" : "s"}`,
    relationship: null,
    pageCount: group.facts.length,
    state: "current",
    changeId: null,
    adjusted: false,
  };
}

function relationshipLabel(plan: MapPlan, change: MapChange): string | null {
  const labels = relatedGroupLabels(plan.data, change.pageFacts, plan.facts);
  return labels[0] ? `Connects toward ${labels[0]}` : null;
}

function mapEmptyReason(
  data: OverviewResponse,
  changes: MapChange[],
): string | null {
  if (data.pages.length === 0) {
    return "Faro found no pages in this scan, so a content estate plan cannot be proposed yet.";
  }
  if (data.pages.length === 1) {
    return "Faro found the current page, but one page is not enough to propose a content estate plan.";
  }
  if (changes.length === 0) {
    return "Faro found the current pages, but there is not enough structural evidence to propose a content estate plan yet.";
  }
  if (
    changes.every((change) => change.status === MAP_CHANGE_STATUS.UNRESOLVED)
  ) {
    return `${changes.length} area${changes.length === 1 ? " needs" : "s need"} client context before a target structure can be proposed.`;
  }
  return null;
}
